import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { LanguageProvider } from './i18n/LanguageStore';
import { RoleDto } from './api/generated/login-generated-api';
import { UhaUserProvider } from './user/UhaUserStore';
import { PsaUserProvider } from './user/PsaUserStore';
import { StudentUserProvider } from './user/StudentUserStore';
import NotificationProvider from './components/notifications/notificationProvider';
import { RenderPageNotFoundProvider } from './pages/pagenotfoundStore';
import { SessionProvider } from './api/sessionExtension';
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

export const SelectedRoleContext = React.createContext<RoleDto | undefined>(
  undefined,
);

const theme = createTheme({
  typography: {
    fontFamily: ['Barlow', 'sans-serif'].join(','),
    h5: {
      fontSize: '20px',
      fontFamily: ['Merriweather', 'serif'].join(','),
    },
    h3: {
      fontSize: '20px',
      fontFamily: ['Merriweather', 'serif'].join(','),
    },
    subtitle1: {
      fontSize: '14px',
    },
  },
  palette: {
    background: {
      default: '#F4F6F7',
    },

    primary: {
      light: '#63ccff',
      main: '#ffffff',
      dark: '#006db3',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          boxShadow: 'none',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '0.875rem',
          lineHeight: 1.43,
          letterSpacing: '0.01071em',
        },
      },
    },
  },
});

const Providers: React.FC<React.PropsWithChildren> = ({ children }) => {
  const router = createBrowserRouter([
    {
      path: '/*',
      element: children,
    },
  ]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LanguageProvider>
          <SessionProvider>
            <StudentUserProvider>
              <UhaUserProvider>
                <PsaUserProvider>
                  <NotificationProvider>
                    <RenderPageNotFoundProvider>
                      <RouterProvider router={router} />
                    </RenderPageNotFoundProvider>
                  </NotificationProvider>
                </PsaUserProvider>
              </UhaUserProvider>
            </StudentUserProvider>
          </SessionProvider>
        </LanguageProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export const withProviders =
  (WithComponent: React.FC) =>
  ({ ...props }) => (
    <Providers>
      <WithComponent {...props} />
    </Providers>
  );
